import '.n{position:relative;}.ssr-plugin'
import '.h{width:100%;}.ssr-plugin'
import '.o{height:100%;}.ssr-plugin'
import '.c6{overflow-y:scroll;}.ssr-plugin'
import '.ci{opacity:0;}.ssr-plugin'
import '.a{display:flex;}.ssr-plugin'
import '.p{position:absolute;}.ssr-plugin'
import '.e3{z-index:2;}.ssr-plugin'
import '.az{appearance:none;}.ssr-plugin'
import '.a8{background:transparent;}.ssr-plugin'
import '.x{border:none;}.ssr-plugin'
import '.fl{padding:4px;}.ssr-plugin'
import '.fm{transition:transform 100ms cubic-bezier(0.2,0,0,1);}.ssr-plugin'
import '.fn{padding:0 20px;}.ssr-plugin'
import '.fc{font-size:13px;}.ssr-plugin'
import '.fd{font-family:Geist Mono;}.ssr-plugin'
import '.fe{line-height:20px;}.ssr-plugin'
import '.ff{counter-increment:line;}.ssr-plugin'
import { Highlight, themes } from 'prism-react-renderer'
import { styled } from 'inlines'
import { colors } from '../../tokens/colors.js'
import { radius } from '../../utils/radius.js'
import { Icon } from '../Icon/index.js'
import { Tooltip } from '../Tooltip/index.js'
import { useTheme } from '../../hooks/useTheme.js'
import { useToast } from '../Toast/index.js'

type CodeBlockProps = {
  code: string
  language: 'js' | 'jsx' | 'ts' | 'tsx' | 'bash'
  fileName?: string
  lineNumbers?: boolean
  highlightedLines?: number[]
  border?: boolean
  copy?: boolean
}

function CodeBlock({
  fileName,
  language,
  highlightedLines,
  code,
  lineNumbers = true,
  border = true,
  copy = true,
}: CodeBlockProps) {
  const { theme } = useTheme()
  const toast = useToast()

  return (
    <styled.div className="n h o c6"
      data-header={fileName ? 'true' : undefined}
      style={{
        ...(border && {
          border: `1px solid ${colors.neutral20Adjusted}`,
          borderRadius: radius[12],
          overflow: 'hidden',
        }),
        '&[data-header] .copyButton, &:hover .copyButton': {
          opacity: '1 !important',
        },
      }}
    >
      {copy && (
        <styled.div className="copyButton ci a p e3"
          style={{
            top: fileName ? 12 : 20,
            right: fileName ? 16 : 20,
            '&:focus-within': {
              opacity: 1,
            },
          }}
        >
          <Tooltip description="Copy code">
            <styled.button className="az a8 x a fl fm"
              style={{
                color: colors.neutral60,
                borderRadius: radius[8],
                margin: -4,
                '&:hover': {
                  background: colors.neutral10Adjusted,
                  color: colors.neutral100,
                },
                '&:active': {
                  transform: 'scale(0.945)',
                },
                '&:focus-visible': {
                  background: colors.neutral10Adjusted,
                  color: colors.neutral100,
                  outline: `${colors.neutral20} solid 4px`,
                },
              }}
              onClick={() => {
                navigator.clipboard.writeText(code)
                toast('Code copied')
              }}
            >
              <Icon variant="copy" />
            </styled.button>
          </Tooltip>
        </styled.div>
      )}
      {fileName && (
        <div
          style={{
            height: 48,
            display: 'flex',
            alignItems: 'center',
            padding: '0 16px',
            boxShadow: `0 1px 0 ${colors.neutral20Adjusted}`,
            background: colors.neutral5,
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontFamily: 'Geist Mono',
              color: colors.neutral60,
            }}
          >
            {fileName}
          </div>
        </div>
      )}
      <Highlight
        theme={theme === 'light' ? themes.oneLight : themes.oneDark}
        code={code}
        language={language}
      >
        {({ tokens, getTokenProps }) => (
          <pre
            style={{
              padding: '20px 0',
              overflow: 'hidden',
              whiteSpace: 'pre-wrap',
            }}
          >
            {tokens.map((line, i) => (
              <styled.div className="fn n fc fd fe ff"
                key={i}
                style={{
                  ...(lineNumbers && {
                    '&:before': {
                      display: 'inline-block',
                      minWidth: 16,
                      textAlign: 'right',
                      marginRight: 20,
                      content: 'counter(line)',
                      color: colors.neutral60,
                    },
                  }),
                  ...(highlightedLines?.includes(i) && {
                    background: colors.neutral5,
                    '&:after': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 2,
                      background: colors.neutral10,
                    },
                  }),
                }}
              >
                {line.map((token, ii) => (
                  <span key={ii} {...getTokenProps({ token })} />
                ))}
              </styled.div>
            ))}
          </pre>
        )}
      </Highlight>
    </styled.div>
  )
}

export { CodeBlock }
export type { CodeBlockProps }
